import { Request, Response } from "express";
import mongoose from "mongoose";

import { Class, Quiz, QuizAttempt, Student, Teacher } from "../models";
import { logger } from "../utils/logger";

interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
  school?: {
    id: string;
    name: string;
    isVerified: boolean;
  };
}

const processFileArray = (files: Express.Multer.File[]) =>
  files.map(file => ({
    fileName: file.filename,
    originalName: file.originalname,
    fileSize: file.size,
    mimeType: file.mimetype,
    fileUrl: `/uploads/documents/${file.filename}`,
    uploadedAt: new Date(),
  }));

class QuizController {
  /**
   * Create a new quiz
   * @route POST /api/v1/school/quizzes
   * @access Private (School Admin, Teacher)
   */
  async createQuiz(req: AuthRequest, res: Response): Promise<void> {
    try {
      const {
        semesterId,
        classId,
        title,
        description,
        duration,
        deadline,
        startTime,
        shuffleQuestions,
        shuffleOptions,
        showResultsImmediately,
        allowMultipleAttempts,
        maxAttempts,
      } = req.body;
      let { questions } = req.body;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const userRole = req.user?.role;

      const materialFiles = (req.files as Record<string, Express.Multer.File[]>)
        ?.quizMaterial;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (typeof questions === "string") {
        try {
          questions = JSON.parse(questions);
        } catch {
          res.status(400).json({
            success: false,
            message: "Invalid questions format. Must be a valid JSON array.",
            error: "Invalid questions",
          } as APIResponse);
          return;
        }
      }

      if (
        !semesterId ||
        !classId ||
        !title ||
        !questions ||
        !duration ||
        !deadline
      ) {
        res.status(400).json({
          success: false,
          message:
            "Semester ID, Class ID, title, questions, duration, and deadline are required",
          error: "Missing required fields",
        } as APIResponse);
        return;
      }

      if (!Array.isArray(questions) || questions.length === 0) {
        res.status(400).json({
          success: false,
          message: "Quiz must have at least one question",
          error: "Invalid questions",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOne({
        _id: classId,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
        semesterId: mongoose.Types.ObjectId.createFromHexString(semesterId),
      });

      if (!classItem) {
        res.status(400).json({
          success: false,
          message:
            "Invalid class ID or class doesn't belong to your school/semester",
          error: "Invalid class",
        } as APIResponse);
        return;
      }

      let teacherId: mongoose.Types.ObjectId;

      if (userRole === "teacher") {
        const teacher = await Teacher.findOne({
          userId: new mongoose.Types.ObjectId(userId),
          schoolId: new mongoose.Types.ObjectId(schoolId),
          isApproved: true,
        });

        if (!teacher) {
          res.status(403).json({
            success: false,
            message: "Teacher profile not found or not approved",
            error: "Unauthorized",
          } as APIResponse);
          return;
        }

        teacherId = teacher._id;
      } else {
        if (!classItem.teacherIds || classItem.teacherIds.length === 0) {
          res.status(400).json({
            success: false,
            message: "Class must have at least one teacher assigned",
            error: "No teacher assigned",
          } as APIResponse);
          return;
        }

        teacherId = classItem.teacherIds[0];
      }

      const materials =
        materialFiles && materialFiles.length > 0
          ? processFileArray(materialFiles)
          : [];

      const newQuiz = new Quiz({
        semesterId: mongoose.Types.ObjectId.createFromHexString(semesterId),
        classId: mongoose.Types.ObjectId.createFromHexString(classId),
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
        teacherId,
        title,
        description,
        questions,
        duration,
        deadline: new Date(deadline),
        startTime: startTime ? new Date(startTime) : undefined,
        shuffleQuestions: shuffleQuestions || false,
        shuffleOptions: shuffleOptions || false,
        showResultsImmediately: showResultsImmediately ?? true,
        allowMultipleAttempts: allowMultipleAttempts || false,
        maxAttempts: allowMultipleAttempts ? maxAttempts || 1 : undefined,
        materials,
        createdBy: mongoose.Types.ObjectId.createFromHexString(userId || ""),
      });

      await newQuiz.save();

      logger.info(`Quiz created: ${newQuiz.id}`, {
        title,
        classId,
        schoolId,
        userId,
      });

      res.status(201).json({
        success: true,
        message: "Quiz created successfully",
        data: {
          quiz: {
            id: newQuiz.id,
            title: newQuiz.title,
            description: newQuiz.description,
            totalMarks: newQuiz.totalMarks,
            duration: newQuiz.duration,
            deadline: newQuiz.deadline,
            status: newQuiz.status,
            questionCount: newQuiz.questions.length,
            materials: newQuiz.materials,
            createdAt: newQuiz.createdAt,
          },
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Error creating quiz:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create quiz",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all quizzes for a class or semester
   * @route GET /api/v1/school/quizzes
   * @access Private (School Admin, Teacher)
   */
  async getAllQuizzes(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { classId, semesterId, status } = req.query;
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      const query: Record<string, unknown> = {
        schoolId: new mongoose.Types.ObjectId(schoolId),
      };

      if (classId) {
        query.classId = new mongoose.Types.ObjectId(classId as string);
      }

      if (semesterId) {
        query.semesterId = new mongoose.Types.ObjectId(semesterId as string);
      }

      if (status) {
        query.status = status;
      }

      const quizzes = await Quiz.find({ ...query })
        .populate("semester", "semesterName academicYear")
        .populate("class", "className classCode")
        .populate("teacher", "fullName employeeId")
        .sort({ deadline: -1 });

      const quizzesWithStats = await Promise.all(
        quizzes.map(async quiz => {
          const totalAttempts = await QuizAttempt.countDocuments({
            quizId: quiz._id,
          });
          const gradedAttempts = await QuizAttempt.countDocuments({
            quizId: quiz._id,
            status: "graded",
          });

          return {
            id: quiz.id,
            title: quiz.title,
            description: quiz.description,
            totalMarks: quiz.totalMarks,
            duration: quiz.duration,
            deadline: quiz.deadline,
            startTime: quiz.startTime,
            status: quiz.status,
            publishedAt: quiz.publishedAt,
            questionCount: quiz.questions.length,
            materials: quiz.materials,
            semester: (
              quiz as unknown as {
                semester: { semesterName: string; academicYear: string };
              }
            ).semester,
            class: (
              quiz as unknown as {
                class: { className: string; classCode: string };
              }
            ).class,
            teacher: (
              quiz as unknown as {
                teacher: { fullName: string; employeeId: string };
              }
            ).teacher,
            attempts: {
              total: totalAttempts,
              graded: gradedAttempts,
              pending: totalAttempts - gradedAttempts,
            },
            createdAt: quiz.createdAt,
          };
        }),
      );

      res.status(200).json({
        success: true,
        message: `${quizzes.length} quizzes found`,
        data: {
          quizzes: quizzesWithStats,
          totalCount: quizzes.length,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching quizzes:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch quizzes",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get quiz by ID (Teacher view - with answers)
   * @route GET /api/v1/school/quizzes/:id
   * @access Private (School Admin, Teacher)
   */
  async getQuizById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid quiz ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const quiz = await Quiz.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      })
        .populate("semester", "semesterName academicYear startDate endDate")
        .populate("class", "className classCode currentStudents")
        .populate("teacher", "fullName employeeId profilePicture");

      if (!quiz) {
        res.status(404).json({
          success: false,
          message: "Quiz not found",
          error: "Quiz not found",
        } as APIResponse);
        return;
      }

      res.status(200).json({
        success: true,
        message: "Quiz found",
        data: {
          quiz: {
            id: quiz.id,
            title: quiz.title,
            description: quiz.description,
            totalMarks: quiz.totalMarks,
            duration: quiz.duration,
            deadline: quiz.deadline,
            startTime: quiz.startTime,
            status: quiz.status,
            publishedAt: quiz.publishedAt,
            questions: quiz.questions,
            shuffleQuestions: quiz.shuffleQuestions,
            shuffleOptions: quiz.shuffleOptions,
            showResultsImmediately: quiz.showResultsImmediately,
            allowMultipleAttempts: quiz.allowMultipleAttempts,
            maxAttempts: quiz.maxAttempts,
            materials: quiz.materials,
            semester: (quiz as unknown as { semester: unknown }).semester,
            class: (quiz as unknown as { class: unknown }).class,
            teacher: (quiz as unknown as { teacher: unknown }).teacher,
            createdAt: quiz.createdAt,
            updatedAt: quiz.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching quiz:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch quiz",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Update quiz
   * @route PUT /api/v1/school/quizzes/:id
   * @access Private (School Admin, Teacher)
   */
  async updateQuiz(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const {
        title,
        description,
        duration,
        deadline,
        startTime,
        shuffleQuestions,
        shuffleOptions,
        showResultsImmediately,
        allowMultipleAttempts,
        maxAttempts,
      } = req.body;
      let { questions } = req.body;
      const schoolId = req.user?.schoolId;

      const materialFiles = (req.files as Record<string, Express.Multer.File[]>)
        ?.quizMaterial;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid quiz ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const quiz = await Quiz.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!quiz) {
        res.status(404).json({
          success: false,
          message: "Quiz not found",
          error: "Quiz not found",
        } as APIResponse);
        return;
      }

      if (quiz.status === "published") {
        const hasAttempts = await QuizAttempt.exists({ quizId: quiz._id });

        if (hasAttempts) {
          res.status(400).json({
            success: false,
            message: "Cannot update published quiz with existing attempts",
            error: "Quiz has attempts",
          } as APIResponse);
          return;
        }
      }

      if (typeof questions === "string") {
        try {
          questions = JSON.parse(questions);
        } catch {
          res.status(400).json({
            success: false,
            message: "Invalid questions format. Must be a valid JSON array.",
            error: "Invalid questions",
          } as APIResponse);
          return;
        }
      }

      if (title !== undefined) quiz.title = title;
      if (description !== undefined) quiz.description = description;
      if (questions !== undefined) quiz.questions = questions;
      if (duration !== undefined) quiz.duration = duration;
      if (deadline !== undefined) quiz.deadline = new Date(deadline);
      if (startTime !== undefined)
        quiz.startTime = startTime ? new Date(startTime) : undefined;
      if (shuffleQuestions !== undefined)
        quiz.shuffleQuestions = shuffleQuestions;
      if (shuffleOptions !== undefined) quiz.shuffleOptions = shuffleOptions;
      if (showResultsImmediately !== undefined)
        quiz.showResultsImmediately = showResultsImmediately;
      if (allowMultipleAttempts !== undefined)
        quiz.allowMultipleAttempts = allowMultipleAttempts;
      if (maxAttempts !== undefined) quiz.maxAttempts = maxAttempts;

      if (materialFiles && materialFiles.length > 0) {
        const newMaterials = processFileArray(materialFiles);
        quiz.materials = [...(quiz.materials || []), ...newMaterials];
      }

      await quiz.save();

      logger.info(`Quiz updated: ${quiz.id}`, {
        title: quiz.title,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Quiz updated successfully",
        data: {
          quiz: {
            id: quiz.id,
            title: quiz.title,
            totalMarks: quiz.totalMarks,
            questionCount: quiz.questions.length,
            status: quiz.status,
            materials: quiz.materials,
            updatedAt: quiz.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error updating quiz:", error);
      res.status(500).json({
        success: false,
        message: "Failed to update quiz",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Delete quiz
   * @route DELETE /api/v1/school/quizzes/:id
   * @access Private (School Admin, Teacher)
   */
  async deleteQuiz(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid quiz ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const hasAttempts = await QuizAttempt.exists({
        quizId: new mongoose.Types.ObjectId(id),
      });

      if (hasAttempts) {
        res.status(400).json({
          success: false,
          message: "Cannot delete quiz with existing attempts",
          error: "Quiz has attempts",
        } as APIResponse);
        return;
      }

      const quiz = await Quiz.findOneAndDelete({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!quiz) {
        res.status(404).json({
          success: false,
          message: "Quiz not found",
          error: "Quiz not found",
        } as APIResponse);
        return;
      }

      logger.info(`Quiz deleted: ${quiz.id}`, {
        title: quiz.title,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Quiz deleted successfully",
        data: {
          deletedQuiz: {
            id: quiz.id,
            title: quiz.title,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error deleting quiz:", error);
      res.status(500).json({
        success: false,
        message: "Failed to delete quiz",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Publish quiz
   * @route POST /api/v1/school/quizzes/:id/publish
   * @access Private (School Admin, Teacher)
   */
  async publishQuiz(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid quiz ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const quiz = await Quiz.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!quiz) {
        res.status(404).json({
          success: false,
          message: "Quiz not found",
          error: "Quiz not found",
        } as APIResponse);
        return;
      }

      if (quiz.status === "published") {
        res.status(400).json({
          success: false,
          message: "Quiz is already published",
          error: "Already published",
        } as APIResponse);
        return;
      }

      await quiz.publish();

      logger.info(`Quiz published: ${quiz.id}`, {
        title: quiz.title,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Quiz published successfully",
        data: {
          quiz: {
            id: quiz.id,
            title: quiz.title,
            status: quiz.status,
            publishedAt: quiz.publishedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error publishing quiz:", error);
      res.status(500).json({
        success: false,
        message: "Failed to publish quiz",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Close quiz
   * @route POST /api/v1/school/quizzes/:id/close
   * @access Private (School Admin, Teacher)
   */
  async closeQuiz(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid quiz ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const quiz = await Quiz.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!quiz) {
        res.status(404).json({
          success: false,
          message: "Quiz not found",
          error: "Quiz not found",
        } as APIResponse);
        return;
      }

      await quiz.close();

      logger.info(`Quiz closed: ${quiz.id}`, {
        title: quiz.title,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Quiz closed successfully",
        data: {
          quiz: {
            id: quiz.id,
            title: quiz.title,
            status: quiz.status,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error closing quiz:", error);
      res.status(500).json({
        success: false,
        message: "Failed to close quiz",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all attempts for a quiz
   * @route GET /api/v1/school/quizzes/:id/attempts
   * @access Private (School Admin, Teacher)
   */
  async getQuizAttempts(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { status } = req.query;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid quiz ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const quiz = await Quiz.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!quiz) {
        res.status(404).json({
          success: false,
          message: "Quiz not found",
          error: "Quiz not found",
        } as APIResponse);
        return;
      }

      const query: Record<string, unknown> = {
        quizId: new mongoose.Types.ObjectId(id),
      };

      if (status) {
        query.status = status;
      }

      const attempts = await QuizAttempt.find({ ...query })
        .populate("student", "fullName studentId profilePicture")
        .populate("gradedBy", "fullName employeeId")
        .sort({ submittedAt: -1 });

      res.status(200).json({
        success: true,
        message: `${attempts.length} attempts found`,
        data: {
          quiz: {
            id: quiz.id,
            title: quiz.title,
            totalMarks: quiz.totalMarks,
          },
          attempts: attempts.map(attempt => ({
            id: attempt.id,
            student: (attempt as unknown as { student: unknown }).student,
            attemptNumber: attempt.attemptNumber,
            startedAt: attempt.startedAt,
            submittedAt: attempt.submittedAt,
            timeTaken: attempt.timeTaken,
            totalMarksObtained: attempt.totalMarksObtained,
            status: attempt.status,
            manualGradingRequired: attempt.manualGradingRequired,
            gradedAt: attempt.gradedAt,
            gradedBy: (attempt as unknown as { gradedBy: unknown }).gradedBy,
          })),
          totalCount: attempts.length,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching quiz attempts:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch attempts",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Grade quiz attempt (manual grading for essay/short answer)
   * @route PUT /api/v1/school/quizzes/:id/attempts/:attemptId/grade
   * @access Private (School Admin, Teacher)
   */
  async gradeQuizAttempt(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id, attemptId } = req.params;
      const { answers, feedback } = req.body;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;

      if (
        !mongoose.Types.ObjectId.isValid(id) ||
        !mongoose.Types.ObjectId.isValid(attemptId)
      ) {
        res.status(400).json({
          success: false,
          message: "Invalid quiz or attempt ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const quiz = await Quiz.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!quiz) {
        res.status(404).json({
          success: false,
          message: "Quiz not found",
          error: "Quiz not found",
        } as APIResponse);
        return;
      }

      const attempt = await QuizAttempt.findOne({
        _id: attemptId,
        quizId: new mongoose.Types.ObjectId(id),
      });

      if (!attempt) {
        res.status(404).json({
          success: false,
          message: "Quiz attempt not found",
          error: "Attempt not found",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!teacher) {
        res.status(403).json({
          success: false,
          message: "Teacher profile not found",
          error: "Unauthorized",
        } as APIResponse);
        return;
      }

      if (answers && Array.isArray(answers)) {
        for (const answer of answers) {
          const attemptAnswer = attempt.answers.find(
            a => a.questionId === answer.questionId,
          );
          if (attemptAnswer && answer.marksObtained !== undefined) {
            attemptAnswer.marksObtained = answer.marksObtained;
          }
        }
      }

      const totalMarks = attempt.answers.reduce(
        (sum, a) => sum + (a.marksObtained || 0),
        0,
      );
      attempt.totalMarksObtained = totalMarks;

      await attempt.grade(feedback || "", teacher._id);

      logger.info(`Quiz attempt graded: ${attempt.id}`, {
        quizId: id,
        totalMarks,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Quiz attempt graded successfully",
        data: {
          attempt: {
            id: attempt.id,
            totalMarksObtained: attempt.totalMarksObtained,
            feedback: attempt.feedback,
            status: attempt.status,
            gradedAt: attempt.gradedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error grading quiz attempt:", error);
      res.status(500).json({
        success: false,
        message: "Failed to grade quiz attempt",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get available quizzes for student
   * @route GET /api/v1/student/quizzes
   * @access Private (Student)
   */
  async getStudentQuizzes(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const { classId } = req.query;

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
        schoolId: new mongoose.Types.ObjectId(schoolId),
        isApproved: true,
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      const studentClasses = await Class.find({
        schoolId: new mongoose.Types.ObjectId(schoolId),
        studentIds: student._id,
      }).select("_id");

      const classIds = studentClasses.map(c => c._id);

      const query: Record<string, unknown> = {
        classId: { $in: classIds },
        status: "published",
      };

      if (classId) {
        query.classId = new mongoose.Types.ObjectId(classId as string);
      }

      const quizzes = await Quiz.find({ ...query })
        .populate("class", "className classCode")
        .populate("semester", "semesterName academicYear")
        .sort({ deadline: 1 });

      const quizzesWithAttempts = await Promise.all(
        quizzes.map(async quiz => {
          const attempts = await QuizAttempt.find({
            quizId: quiz._id,
            studentId: student._id,
          }).sort({ attemptNumber: -1 });

          const bestAttempt = attempts.find(a => a.status === "graded");

          let derivedStatus = "pending";
          if (attempts.length > 0) {
            const latestAttempt = attempts[0];
            switch (latestAttempt.status) {
              case "graded": {
                derivedStatus = "graded";
                break;
              }
              case "submitted": {
                derivedStatus = "submitted";
                break;
              }
              case "in_progress": {
                derivedStatus = "in_progress";
                break;
              }
            }
          } else if (quiz.isOverdue()) {
            derivedStatus = "overdue";
          }

          return {
            id: quiz.id,
            title: quiz.title,
            description: quiz.description,
            totalMarks: quiz.totalMarks,
            duration: quiz.duration,
            deadline: quiz.deadline,
            startTime: quiz.startTime,
            questionCount: quiz.questions.length,
            allowMultipleAttempts: quiz.allowMultipleAttempts,
            maxAttempts: quiz.maxAttempts,
            materials: quiz.materials,
            class: (
              quiz as unknown as {
                class: { className: string; classCode: string };
              }
            ).class,
            semester: (
              quiz as unknown as {
                semester: { semesterName: string; academicYear: string };
              }
            ).semester,
            status: derivedStatus,
            attempts: {
              count: attempts.length,
              canAttempt:
                quiz.canAttempt() &&
                (!quiz.maxAttempts || attempts.length < quiz.maxAttempts),
              lastAttempt: attempts[0]
                ? {
                    attemptNumber: attempts[0].attemptNumber,
                    status: attempts[0].status,
                    totalMarksObtained: attempts[0].totalMarksObtained,
                    submittedAt: attempts[0].submittedAt,
                  }
                : null,
              bestScore: bestAttempt?.totalMarksObtained || null,
            },
            isAvailable: quiz.isAvailable(),
            isOverdue: quiz.isOverdue(),
          };
        }),
      );

      res.status(200).json({
        success: true,
        message: `${quizzes.length} quizzes found`,
        data: {
          quizzes: quizzesWithAttempts,
          totalCount: quizzes.length,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching student quizzes:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch quizzes",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get quiz for student (without answers)
   * @route GET /api/v1/student/quizzes/:id
   * @access Private (Student)
   */
  async getStudentQuizById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid quiz ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      const quiz = await Quiz.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
        status: "published",
      })
        .populate("class", "className classCode")
        .populate("semester", "semesterName academicYear");

      if (!quiz) {
        res.status(404).json({
          success: false,
          message: "Quiz not found or not published",
          error: "Quiz not found",
        } as APIResponse);
        return;
      }

      const questionsWithoutAnswers = quiz.questions.map((q: any) => ({
        _id: q._id,
        questionText: q.questionText,
        questionType: q.questionType,
        options: q.options,
        marks: q.marks,
        order: q.order,
      }));

      res.status(200).json({
        success: true,
        message: "Quiz found",
        data: {
          quiz: {
            id: quiz.id,
            title: quiz.title,
            description: quiz.description,
            totalMarks: quiz.totalMarks,
            duration: quiz.duration,
            deadline: quiz.deadline,
            startTime: quiz.startTime,
            questions: questionsWithoutAnswers,
            shuffleQuestions: quiz.shuffleQuestions,
            shuffleOptions: quiz.shuffleOptions,
            showResultsImmediately: quiz.showResultsImmediately,
            allowMultipleAttempts: quiz.allowMultipleAttempts,
            maxAttempts: quiz.maxAttempts,
            materials: quiz.materials,
            class: (quiz as unknown as { class: unknown }).class,
            semester: (quiz as unknown as { semester: unknown }).semester,
            isAvailable: quiz.isAvailable(),
            canAttempt: quiz.canAttempt(),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching quiz for student:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch quiz",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Start quiz attempt
   * @route POST /api/v1/student/quizzes/:id/start
   * @access Private (Student)
   */
  async startQuizAttempt(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid quiz ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
        schoolId: new mongoose.Types.ObjectId(schoolId),
        isApproved: true,
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      const quiz = await Quiz.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
        status: "published",
      });

      if (!quiz) {
        res.status(404).json({
          success: false,
          message: "Quiz not found or not published",
          error: "Quiz not found",
        } as APIResponse);
        return;
      }

      if (!quiz.canAttempt()) {
        res.status(400).json({
          success: false,
          message: "Quiz is not available for attempt",
          error: "Quiz not available",
        } as APIResponse);
        return;
      }

      const attemptCount = await QuizAttempt.countDocuments({
        quizId: quiz._id,
        studentId: student._id,
      });

      if (quiz.maxAttempts && attemptCount >= quiz.maxAttempts) {
        res.status(400).json({
          success: false,
          message: `Maximum ${quiz.maxAttempts} attempts allowed`,
          error: "Max attempts reached",
        } as APIResponse);
        return;
      }

      const inProgressAttempt = await QuizAttempt.findOne({
        quizId: quiz._id,
        studentId: student._id,
        status: "in_progress",
      });

      if (inProgressAttempt) {
        res.status(400).json({
          success: false,
          message:
            "You have an in-progress attempt. Please complete or abandon it first",
          error: "In-progress attempt exists",
        } as APIResponse);
        return;
      }

      const attempt = new QuizAttempt({
        quizId: quiz._id,
        studentId: student._id,
        classId: quiz.classId,
        attemptNumber: attemptCount + 1,
        answers: [],
      });

      await attempt.save();

      let questions = quiz.questions.map((q: any) => ({
        _id: q._id,
        questionText: q.questionText,
        questionType: q.questionType,
        options: q.options,
        marks: q.marks,
        order: q.order,
      }));

      if (quiz.shuffleQuestions) {
        questions = questions.sort(() => Math.random() - 0.5);
      }

      logger.info(`Quiz attempt started: ${attempt.id}`, {
        quizId: id,
        studentId: student.id,
        attemptNumber: attempt.attemptNumber,
      });

      res.status(201).json({
        success: true,
        message: "Quiz attempt started",
        data: {
          attempt: {
            id: attempt.id,
            attemptNumber: attempt.attemptNumber,
            startedAt: attempt.startedAt,
            duration: quiz.duration,
            expiresAt: new Date(
              attempt.startedAt.getTime() + quiz.duration * 60_000,
            ),
          },
          quiz: {
            id: quiz.id,
            title: quiz.title,
            totalMarks: quiz.totalMarks,
            duration: quiz.duration,
            questions,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error starting quiz attempt:", error);
      res.status(500).json({
        success: false,
        message: "Failed to start quiz attempt",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Submit quiz attempt
   * @route POST /api/v1/student/quizzes/:id/submit
   * @access Private (Student)
   */
  async submitQuizAttempt(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { attemptId, answers: answersString } = req.body;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const files = req.files as Express.Multer.File[];

      if (!mongoose.Types.ObjectId.isValid(id) || !attemptId) {
        res.status(400).json({
          success: false,
          message: "Invalid quiz or attempt ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      let answers: any[];
      try {
        answers =
          typeof answersString === "string"
            ? JSON.parse(answersString)
            : answersString;
      } catch {
        res.status(400).json({
          success: false,
          message: "Invalid answers format",
          error: "Answers must be valid JSON array",
        } as APIResponse);
        return;
      }

      if (!answers || !Array.isArray(answers)) {
        res.status(400).json({
          success: false,
          message: "Answers array is required",
          error: "Missing answers",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      const attempt = await QuizAttempt.findOne({
        _id: attemptId,
        quizId: new mongoose.Types.ObjectId(id),
        studentId: student._id,
        status: "in_progress",
      });

      if (!attempt) {
        res.status(404).json({
          success: false,
          message: "Quiz attempt not found or already submitted",
          error: "Attempt not found",
        } as APIResponse);
        return;
      }

      const fileData =
        files?.map(file => ({
          fileName: file.filename,
          originalName: file.originalname,
          fileSize: file.size,
          mimeType: file.mimetype,
          fileUrl: `/uploads/documents/${file.filename}`,
          uploadedAt: new Date(),
        })) || [];

      const answersWithFiles = answers.map(answer => {
        const answerFiles = fileData.filter(file =>
          file.originalName?.startsWith(`q${answer.questionId}_`),
        );

        return {
          ...answer,
          files: answerFiles.length > 0 ? answerFiles : undefined,
        };
      });

      attempt.answers = answersWithFiles;

      await attempt.submit();
      await attempt.autoGrade();

      logger.info(`Quiz attempt submitted: ${attempt.id}`, {
        quizId: id,
        studentId: student.id,
        autoGradedMarks: attempt.autoGradedMarks,
      });

      res.status(200).json({
        success: true,
        message: "Quiz submitted successfully",
        data: {
          attempt: {
            id: attempt.id,
            attemptNumber: attempt.attemptNumber,
            submittedAt: attempt.submittedAt,
            timeTaken: attempt.timeTaken,
            autoGradedMarks: attempt.autoGradedMarks,
            totalMarksObtained: attempt.totalMarksObtained,
            manualGradingRequired: attempt.manualGradingRequired,
            status: attempt.status,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error submitting quiz attempt:", error);
      res.status(500).json({
        success: false,
        message: "Failed to submit quiz attempt",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get student's quiz results
   * @route GET /api/v1/student/quizzes/:id/results
   * @access Private (Student)
   */
  async getQuizResults(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid quiz ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      const quiz = await Quiz.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      }).populate("class", "className classCode");

      if (!quiz) {
        res.status(404).json({
          success: false,
          message: "Quiz not found",
          error: "Quiz not found",
        } as APIResponse);
        return;
      }

      const attempts = await QuizAttempt.find({
        quizId: quiz._id,
        studentId: student._id,
      }).sort({ attemptNumber: -1 });

      const gradedScores = attempts
        .filter(a => a.totalMarksObtained !== undefined)
        .map(a => a.totalMarksObtained || 0);

      res.status(200).json({
        success: true,
        message: `${attempts.length} attempts found`,
        data: {
          quiz: {
            id: quiz.id,
            title: quiz.title,
            totalMarks: quiz.totalMarks,
            class: (quiz as unknown as { class: unknown }).class,
          },
          attempts: attempts.map(attempt => ({
            id: attempt.id,
            attemptNumber: attempt.attemptNumber,
            startedAt: attempt.startedAt,
            submittedAt: attempt.submittedAt,
            timeTaken: attempt.timeTaken,
            totalMarksObtained: attempt.totalMarksObtained,
            answers: attempt.answers,
            feedback: attempt.feedback,
            status: attempt.status,
            gradedAt: attempt.gradedAt,
          })),
          bestScore: gradedScores.length > 0 ? Math.max(...gradedScores) : null,
          totalAttempts: attempts.length,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching quiz results:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch quiz results",
        error: "Internal server error",
      } as APIResponse);
    }
  }
}

export const quizController = new QuizController();
